import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import cs from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Manufacturer,
  CreateVehicle,
  EditVehicle,
  RemoveVehicle,
} from './CustomCells';
import DataTable from '../DataTable/DataTable';
import CustomTableFilters from '../TableFilters/CustomTableFilters';
import styles from './Vehicles.module.scss';
import {
  getVehicles,
  storeVehicle,
  updateVehicle,
  removeVehicle,
} from '../../redux/modules/vehicles';
import { getCarManufacturers } from '../../redux/modules/carMarks';
import { showNotification } from '../../helpers/notificationHelpers';

const cols = [
  {
    title: 'vehicleTbl.modelName',
    property: 'modelName',
    sortable: true,
    filter: { type: 'string' },
  },
];

const Vehicles = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tableRef = useRef();

  const createDialogTitle = t('vehicleModal.create');
  const editDialogTitle = t('vehicleModal.edit');

  const { manufacturers } = useSelector((store) => store.carMarks, shallowEqual);
  const { vehicles, isLoading } = useSelector(
    (store) => store.vehiclesReducer,
    shallowEqual,
  );

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [filters, setFilters] = useState({});
  const [editVehicleDialogOpen, setEditVehicleDialogOpen] = useState(false);
  const [createVehicleDialogOpen, setCreateVehicleDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(getCarManufacturers());
  }, [dispatch]);

  const createHandler = (data) => dispatch(storeVehicle(data)).then(() => {
    tableRef.current.loadCurrentData();
    showNotification(
      'vehicleModal.storeSuccess',
      'success',
      'STORE_VEHICLE',
    );
  });

  const editHandler = (data) => dispatch(updateVehicle(data)).then(() => {
    tableRef.current.loadCurrentData();
    showNotification(
      'vehicleModal.updateSuccess',
      'success',
      'UPDATE_VEHICLE',
    );
  });

  const removeHandler = (data) => {
    if (window.confirm(t('vehicleModal.confirmDelete'))) {
      return dispatch(removeVehicle(data.modelId)).then(() => {
        showNotification(
          'vehicleModal.deleteSuccess',
          'success',
          'DELETE_VEHICLE',
        );
      }, () => { });
    }
  };

  const columns = [
    {
      title: 'vehicleTbl.manufacturerName',
      property: 'manufacturerName',
      sortable: true,
      customCell: (r, c, p, getSrt) => Manufacturer(r, c, p, getSrt, editHandler, removeHandler, editVehicleDialogOpen, setEditVehicleDialogOpen, editDialogTitle, manufacturers),
      filter: { type: 'string' },
    },
    ...cols,
    {
      title: '',
      sortable: false,
      customCell: (row) => EditVehicle(editDialogTitle, editHandler, editVehicleDialogOpen, setEditVehicleDialogOpen, manufacturers, row),
    },
    {
      title: '',
      sortable: false,
      customCell: (row) => RemoveVehicle(row, removeHandler),
    },
  ];

  const toggleFiltersBlock = () => {
    setIsCollapsed(!isCollapsed);
  };

  const loadData = useCallback(
    (params) => {
      dispatch(getVehicles({ params, filters }));
    },
    [dispatch, filters],
  );

  return (
    <div id={styles.vehiclesContainer}>
      <div className={cs(styles.vehiclesHeader, styles.mobileContent)}>
        {t('headerTitles.vehicles')}
      </div>

      <div className={cs(styles.vehiclesTitleRow, 'headerRow')}>
        <div className="header">
          {t('headerTitles.vehicles')}
        </div>
        <div>
          {CreateVehicle(createDialogTitle, createHandler, createVehicleDialogOpen, setCreateVehicleDialogOpen, manufacturers)}
          <button
            label="icon"
            type="button"
            className={cs({
              [styles.filterIcon]: true,
              [styles.expanded]: !isCollapsed,
              white_button: true,
            })}
            onClick={toggleFiltersBlock}
          />
        </div>
      </div>

      {!isCollapsed && (
        <CustomTableFilters
          filters={filters}
          setFilters={setFilters}
          columns={columns}
          itemsPerRow={2}
        />
      )}

      <DataTable
        ref={tableRef}
        data={vehicles.data}
        totalCount={vehicles.total}
        columns={columns}
        onParamsChanges={loadData}
        loading={isLoading}
      />
    </div>
  );
};

export default Vehicles;
